import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

const MobileDrawer = ({ shouldScaleBackground = true, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
MobileDrawer.displayName = "MobileDrawer";

const MobileDrawerTrigger = DrawerPrimitive.Trigger;

const MobileDrawerPortal = DrawerPrimitive.Portal;

const MobileDrawerClose = DrawerPrimitive.Close;

const MobileDrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay 
    ref={ref} 
    className={cn("fixed inset-0 z-50 bg-black/60 backdrop-blur-sm", className)} 
    {...props} 
  />
));
MobileDrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const MobileDrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <MobileDrawerPortal>
    <MobileDrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-3xl border-t border-border bg-card",
        "max-h-[85vh] pb-safe-bottom",
        className,
      )}
      {...props}
    >
      <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
      <div className="overflow-y-auto scrollbar-thin">{children}</div>
    </DrawerPrimitive.Content>
  </MobileDrawerPortal>
));
MobileDrawerContent.displayName = "MobileDrawerContent";

const MobileDrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 p-5 pb-2", className)} {...props} />
);
MobileDrawerHeader.displayName = "MobileDrawerHeader";

const MobileDrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 p-5 pt-2", className)} {...props} />
);
MobileDrawerFooter.displayName = "MobileDrawerFooter";

const MobileDrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight text-foreground", className)}
    {...props}
  />
));
MobileDrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const MobileDrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description 
    ref={ref} 
    className={cn("text-sm text-muted-foreground", className)} 
    {...props} 
  />
));
MobileDrawerDescription.displayName = DrawerPrimitive.Description.displayName;

// Premium Option Item for selections
interface MobileDrawerOptionProps {
  label: string;
  description?: string;
  selected?: boolean;
  onSelect: () => void;
  icon?: React.ReactNode;
}

const MobileDrawerOption = ({ label, description, selected, onSelect, icon }: MobileDrawerOptionProps) => (
  <button
    onClick={onSelect}
    className={cn(
      "w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all",
      "hover:bg-surface-hover active:scale-[0.98]",
      selected && "bg-primary/10 border border-primary/30"
    )}
  >
    {icon && (
      <div className={cn(
        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
        selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
      )}>
        {icon}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className={cn("font-medium", selected && "text-primary")}>{label}</p>
      {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
    </div>
    {selected && (
      <Check className="w-5 h-5 text-primary shrink-0" />
    )}
  </button>
);

export {
  MobileDrawer,
  MobileDrawerPortal,
  MobileDrawerOverlay,
  MobileDrawerTrigger,
  MobileDrawerClose,
  MobileDrawerContent,
  MobileDrawerHeader,
  MobileDrawerFooter,
  MobileDrawerTitle,
  MobileDrawerDescription,
  MobileDrawerOption,
};
